/**
 * Stack module for RPN Calculator
 * Implements an unlimited numeric stack with undo capability
 *
 * Bug fixes applied:
 * - #5: swapxy() now uses array spread [...this.cards] instead of reference assignment
 */

export class Stack {
  private cards: number[] = [0, 0, 0, 0, 0];
  private undostack: number[] = [0, 0, 0, 0, 0];

  constructor(initialCards?: number[]) {
    if (initialCards) {
      this.cards = [...initialCards];
      this.maintain();
    }
  }

  /**
   * Get the current stack as an array
   */
  getCards(): number[] {
    return [...this.cards];
  }

  /**
   * Get value at specific index (0 = X register)
   */
  get(index: number): number {
    return this.cards[index] ?? 0;
  }

  /**
   * Set value at specific index
   */
  set(index: number, value: number): void {
    this.cards[index] = value;
    this.maintain();
  }

  /**
   * Get the X register (bottom of stack, index 0)
   */
  get x(): number {
    return this.cards[0];
  }

  /**
   * Set the X register
   */
  set x(value: number) {
    this.cards[0] = value;
  }

  /**
   * Get the Y register (index 1)
   */
  get y(): number {
    return this.cards[1];
  }

  /**
   * Set the Y register
   */
  set y(value: number) {
    this.cards[1] = value;
  }

  /**
   * Get stack length
   */
  get length(): number {
    return this.cards.length;
  }

  /**
   * Maintain stack integrity - ensure minimum 5 elements, remove trailing zeros
   */
  private maintain(): void {
    // Remove trailing zeros beyond position 4
    let count = this.cards.length - 1;
    while (count > 4 && this.cards[count] === 0) {
      this.cards.pop();
      count--;
    }

    // Ensure minimum 5 elements
    const finalLength = this.cards.length;
    if (finalLength < 5) {
      for (let i = 0; i < 5 - finalLength; i++) {
        this.cards.push(0);
      }
    }
  }

  /**
   * Save current stack state to undo buffer
   * FIX #5: Use spread operator to create a proper copy
   */
  private fillUndoStack(): void {
    this.undostack = [...this.cards];
  }

  /**
   * Push X register value, lifting the stack
   */
  pushX(): void {
    const xValue = this.cards[0];
    // Prevent invalid values from being pushed
    if (!Number.isFinite(xValue)) {
      return;
    }
    this.fillUndoStack();
    this.cards.unshift(xValue);
    this.maintain();
  }

  /**
   * Drop one element and set X to new value
   */
  dropOne(data: number): void {
    this.fillUndoStack();
    this.cards.shift();
    this.cards[0] = data;
    this.maintain();
  }

  /**
   * Roll stack up - bottom element moves to top
   */
  rollUp(): void {
    this.fillUndoStack();
    const removedElement = this.cards.pop()!;
    this.cards.unshift(removedElement);
    this.maintain();
  }

  /**
   * Roll stack down - top element moves to bottom
   */
  rollDown(): void {
    this.fillUndoStack();
    const removedElement = this.cards.shift()!;
    this.cards.push(removedElement);
    this.maintain();
  }

  /**
   * Clear the entire stack
   */
  clear(): void {
    this.fillUndoStack();
    this.cards = [0, 0, 0, 0, 0];
    this.maintain();
  }

  /**
   * Swap X and Y registers
   * FIX #5: Use spread operator for undostack instead of reference assignment
   */
  swapXY(): void {
    // FIX: Create a proper copy, not a reference
    this.undostack = [...this.cards];
    const temp = this.cards[0];
    this.cards[0] = this.cards[1];
    this.cards[1] = temp;
    this.maintain();
  }

  /**
   * Undo last operation
   */
  undo(): void {
    this.cards = [...this.undostack];
    this.maintain();
  }

  /**
   * Serialize stack for storage
   */
  toJSON(): number[] {
    return [...this.cards];
  }

  /**
   * Load stack from storage
   */
  fromJSON(data: number[]): void {
    if (Array.isArray(data)) {
      this.cards = [...data];
      this.maintain();
    }
  }

  /**
   * Debug: dump stack contents
   */
  dump(): string {
    return `Stack: ${this.cards.join(' ')}`;
  }
}
